/*
  Carnevore Mountain shop pages (collection templates).

  - <cm-collection-grid>: filters, sort and pages. Every chip, option and page link is a real link to the filtered
    URL (tag filtering in the path, Search & Discovery and sort in the query), so the page works without JavaScript.
    Here, a click loads that URL's grid through the Section Rendering API (?section_id=) and swaps it in, the same
    pattern as Dawn's facets.js: results are cached by URL, the address bar follows (history.pushState) and the back
    and forward buttons re-render. A newer request cancels an older one, and a response that isn't the latest is
    ignored, so a slow answer never paints over a newer one. The result count is announced. The filter sheet
    previews choices (its options and "Show N products" refresh) and only applies them on that button; closing it
    discards them. Focus is trapped in the sheet and returns to the Filter button.
  - The cards' add to cart (<cm-quick-add>) is in assets/cm-quick-add.js, shared with the product page, so every add
    on the site runs through one queue.
  - <cm-product-row>: previous and next buttons for a swipe row of cards. No autoplay.
  - FAQ answers slide open and closed; content below the fold fades in once. Both are off with reduced motion.
*/
(() => {
  if (window.cmCollection) return;
  window.cmCollection = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Filters, sort and pages ---------- */
  class CmCollectionGrid extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.seq = 0;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.baseUrl = this.dataset.baseUrl;
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);
      this.renderedUrl = window.location.href.split('#')[0];
      // The page as first loaded is part of the history, so the back button can return to it.
      if (!history.state || history.state.cmGrid !== this.sectionId) {
        history.replaceState({ ...(history.state || {}), cmGrid: this.sectionId }, '', window.location.href);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      if (this.controller) this.controller.abort();
    }

    get sheet() {
      return this.querySelector('[data-cm-sheet]');
    }

    get sheetOpen() {
      return this.sheet && !this.sheet.hidden;
    }

    onClick(event) {
      const opener = event.target.closest('[data-cm-open]');
      if (opener) {
        event.preventDefault();
        this.openSheet(opener);
        return;
      }
      if (event.target.closest('[data-cm-close]')) {
        event.preventDefault();
        this.closeSheet();
        return;
      }
      const apply = event.target.closest('[data-cm-apply]');
      if (apply) {
        event.preventDefault();
        const url = this.stagedUrl;
        this.closeSheet({ keepStaged: true });
        if (url && url !== this.currentUrl()) this.navigate(url, { focusKey: 'open' });
        else this.focusOpener();
        return;
      }
      const link = event.target.closest('a[data-cm-nav]');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      if (this.sheetOpen && this.sheet.contains(link)) {
        this.preview(link.href, link.dataset.cmKey);
      } else {
        this.navigate(link.href, { focusKey: link.dataset.cmKey });
      }
    }

    // Sort choices: in the bar they apply at once, in the sheet they preview.
    onChange(event) {
      const form = event.target.closest('form[data-cm-sort]');
      if (!form) return;
      const url = this.formUrl(form);
      if (this.sheetOpen && this.sheet.contains(form)) this.preview(url, event.target.dataset.cmKey);
      else this.navigate(url, { focusKey: event.target.dataset.cmKey });
    }

    onSubmit(event) {
      const form = event.target.closest('form[data-cm-sort], form[data-cm-price]');
      if (!form) return;
      event.preventDefault();
      const url = this.formUrl(form);
      if (this.sheetOpen && this.sheet.contains(form)) this.preview(url, 'price');
      else this.navigate(url);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.sheetOpen) {
        event.preventDefault();
        this.closeSheet();
      }
    }

    onPopState() {
      if (!window.location.pathname.startsWith(this.baseUrl)) return;
      if (window.location.href.split('#')[0] === this.renderedUrl) return; // only the #anchor changed
      this.closeSheet();
      this.navigate(window.location.href, { push: false });
    }

    formUrl(form) {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) if (value !== '') params.append(key, value);
      const query = params.toString();
      return new URL(form.getAttribute('action') + (query ? `?${query}` : ''), window.location.origin).href;
    }

    currentUrl() {
      return window.location.href;
    }

    // The section's HTML for a URL, from the cache or the Section Rendering API.
    load(url) {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', this.sectionId);
      const key = target.href;
      if (this.cache.has(key)) return Promise.resolve(this.cache.get(key));
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      return fetch(key, { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const next = doc.querySelector('cm-collection-grid');
          if (!next) throw new Error('No grid in the response');
          if (this.cache.size > 24) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(key, next);
          return next;
        });
    }

    // Loads a URL and swaps the whole grid in. Only the latest request paints.
    navigate(url, { push = true, focusKey } = {}) {
      const seq = ++this.seq;
      if (!focusKey && this.contains(document.activeElement)) focusKey = document.activeElement.dataset.cmKey;
      const results = this.querySelector('[data-cm-results]');
      if (results) {
        results.style.minHeight = `${results.offsetHeight}px`;
        results.setAttribute('aria-busy', 'true');
      }
      this.classList.add('is-loading');
      return this.load(url)
        .then((next) => {
          if (seq !== this.seq) return;
          const active = document.activeElement;
          const hadFocus = this.contains(active);
          this.replaceChildren(...Array.from(next.cloneNode(true).childNodes));
          if (push) history.pushState({ cmGrid: this.sectionId }, '', url);
          this.renderedUrl = new URL(url, window.location.origin).href.split('#')[0];
          this.keepGridInView();
          if (hadFocus || focusKey) this.restoreFocus(focusKey);
          this.announce();
        })
        .catch((error) => {
          if (error.name === 'AbortError' || seq !== this.seq) return;
          // Anything unexpected: load the page the normal way.
          window.location.assign(url);
        })
        .finally(() => {
          if (seq !== this.seq) return;
          this.classList.remove('is-loading');
          const fresh = this.querySelector('[data-cm-results]');
          if (fresh) {
            fresh.style.minHeight = '';
            fresh.removeAttribute('aria-busy');
          }
        });
    }

    // Sheet choices: refresh the sheet's options and its "Show N products" button, and keep the URL for "apply".
    preview(url, focusKey) {
      const seq = ++this.seq;
      const body = this.sheet.querySelector('[data-cm-sheet-body]');
      body.setAttribute('aria-busy', 'true');
      this.sheet.classList.add('is-loading');
      return this.load(url)
        .then((next) => {
          if (seq !== this.seq || !this.sheetOpen) return;
          const nextBody = next.querySelector('[data-cm-sheet-body]');
          const nextApply = next.querySelector('[data-cm-apply]');
          const nextClear = next.querySelector('.cm-sheet__clear');
          if (nextBody) body.replaceChildren(...Array.from(nextBody.cloneNode(true).childNodes));
          const apply = this.sheet.querySelector('[data-cm-apply]');
          if (nextApply && apply) {
            apply.textContent = nextApply.textContent;
            apply.dataset.count = nextApply.dataset.count;
          }
          const clear = this.sheet.querySelector('.cm-sheet__clear');
          if (nextClear && clear) clear.setAttribute('href', nextClear.getAttribute('href'));
          this.stagedUrl = url;
          this.restoreFocus(focusKey, this.sheet);
          this.announce(apply ? apply.textContent.trim() : '');
        })
        .catch((error) => {
          if (error.name === 'AbortError' || seq !== this.seq) return;
          window.location.assign(url);
        })
        .finally(() => {
          if (seq !== this.seq) return;
          body.removeAttribute('aria-busy');
          this.sheet.classList.remove('is-loading');
        });
    }

    openSheet(opener) {
      const sheet = this.sheet;
      if (!sheet) return;
      this.opener = opener;
      this.stagedUrl = this.currentUrl();
      this.savedBody = sheet.querySelector('[data-cm-sheet-body]').cloneNode(true);
      this.savedApply = sheet.querySelector('[data-cm-apply]').textContent;
      sheet.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      requestAnimationFrame(() => {
        sheet.classList.add('is-open');
        const panel = sheet.querySelector('.cm-sheet__panel');
        if (typeof trapFocus === 'function') trapFocus(panel, panel);
        else panel.focus();
      });
    }

    closeSheet({ keepStaged = false } = {}) {
      const sheet = this.sheet;
      if (!sheet || sheet.hidden) return;
      // Closing without applying discards the choices: put the sheet back as it was.
      if (!keepStaged && this.savedBody) {
        const body = sheet.querySelector('[data-cm-sheet-body]');
        body.replaceWith(this.savedBody);
        sheet.querySelector('[data-cm-apply]').textContent = this.savedApply;
      }
      this.savedBody = null;
      this.seq++; // drop a preview still on its way
      sheet.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      const opener = this.querySelector('[data-cm-open]');
      if (opener) opener.setAttribute('aria-expanded', 'false');
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      const hide = () => {
        sheet.hidden = true;
      };
      if (reducedMotion.matches) hide();
      else setTimeout(hide, 250);
      if (!keepStaged) this.focusOpener();
    }

    focusOpener() {
      const opener = this.querySelector('[data-cm-open]');
      if (opener) opener.focus();
    }

    // After a repaint, put focus back on the control with the same key (or a sensible neighbour).
    restoreFocus(key, scope = this) {
      let target = null;
      if (key === 'open') target = scope.querySelector('[data-cm-open]');
      else if (key) target = scope.querySelector(`[data-cm-key="${CSS.escape(key)}"]`);
      if (target && target.matches('[aria-disabled="true"]')) target = null;
      if (!target && scope === this) {
        target = this.querySelector('.cm-applied a') || this.querySelector('.cm-chip.is-active') || this.querySelector('[data-cm-open]');
      }
      if (target) target.focus({ preventScroll: true });
    }

    // If the shopper was further down the grid than the new results reach, bring its top back into view.
    keepGridInView() {
      const results = this.querySelector('[data-cm-results]');
      const bar = this.querySelector('[data-cm-bar]');
      if (!results) return;
      const barBottom = bar ? bar.getBoundingClientRect().bottom : 0;
      const top = results.getBoundingClientRect().top;
      if (top >= barBottom) return;
      const header = document.querySelector('cm-sticky-header, sticky-header');
      if (header) header.dispatchEvent(new Event('preventHeaderReveal'));
      const barHeight = bar ? bar.offsetHeight : 0;
      window.scrollTo({ top: window.scrollY + top - barHeight - 12, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    announce(text) {
      const region = this.querySelector('[data-cm-announce]');
      if (!region) return;
      const message = text || (this.querySelector('.cm-shopbar__count') || {}).textContent || '';
      region.textContent = '';
      setTimeout(() => {
        region.textContent = message.trim();
      }, 100);
    }
  }
  if (!customElements.get('cm-collection-grid')) customElements.define('cm-collection-grid', CmCollectionGrid);

  /* ---------- Swipe row buttons ---------- */
  class CmProductRow extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('[data-cm-track]');
      this.prev = this.querySelector('[data-cm-prev]');
      this.next = this.querySelector('[data-cm-next]');
      if (!this.track || !this.prev || !this.next) return;
      const step = (direction) => {
        const card = this.track.querySelector(':scope > *');
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const width = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
        this.track.scrollBy({ left: direction * width, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };
      this.prev.addEventListener('click', () => step(-1));
      this.next.addEventListener('click', () => step(1));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        this.prev.disabled = this.track.scrollLeft <= 2;
        this.next.disabled = this.track.scrollLeft >= max;
        this.classList.toggle('is-static', max <= 0);
      };
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }
  }
  if (!customElements.get('cm-product-row')) customElements.define('cm-product-row', CmProductRow);

  /* ---------- FAQ answers slide open and closed ---------- */
  function initAccordion(details) {
    if (details.dataset.cmReady) return;
    details.dataset.cmReady = 'true';
    const summary = details.querySelector('summary');
    const answer = details.querySelector('[data-cm-answer]');
    if (!summary || !answer || !answer.animate) return;
    let busy = false;
    summary.addEventListener('click', (event) => {
      if (reducedMotion.matches) return;
      event.preventDefault();
      if (busy) return;
      busy = true;
      const opening = !details.open;
      if (opening) details.open = true;
      const height = answer.scrollHeight;
      answer.style.overflow = 'hidden';
      const frames = [{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }];
      const animation = answer.animate(opening ? frames : frames.reverse(), { duration: 220, easing: 'ease' });
      animation.onfinish = animation.oncancel = () => {
        if (!opening) details.open = false;
        answer.style.overflow = '';
        busy = false;
      };
    });
  }

  /* ---------- Fade in below the fold ---------- */
  let observer = null;
  function reveal(root) {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) return;
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    }
    root.querySelectorAll('[data-cm-reveal]').forEach((element) => {
      if (element.dataset.cmRevealSeen) return;
      element.dataset.cmRevealSeen = 'true';
      if (element.getBoundingClientRect().top < window.innerHeight) return; // already on screen: leave it be
      element.classList.add('cm-rv');
      observer.observe(element);
    });
  }

  function init(root) {
    root.querySelectorAll('[data-cm-accordion] details').forEach(initAccordion);
    reveal(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
